import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Space, Typography, Table, Tag, message, Modal, Form, Input, Statistic, Row, Col, Tabs, Empty, Select, Alert, Spin, Progress } from 'antd';
import { DollarOutlined, WalletOutlined, CopyOutlined, ReloadOutlined, GiftOutlined, FileTextOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, PieChartOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import dayjs from 'dayjs';

// Number formatting helpers
const formatMoney = (amount: number | string, decimals: number = 2): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '$0.00';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatNumber = (num: number | string): string => {
  const n = typeof num === 'string' ? parseFloat(num) : num;
  if (isNaN(n)) return '0';
  return n.toLocaleString('en-US');
};

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  success: '#10B981',
  warning: '#F59E0B',
};

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  /* Select dropdown styling */
  .ant-select {
    .ant-select-selector {
      background: ${COLORS.bgPrimary} !important;
      border-color: ${COLORS.border} !important;
      color: ${COLORS.textPrimary} !important;
    }
    
    .ant-select-selection-item {
      color: ${COLORS.textPrimary} !important;
    }
    
    .ant-select-selection-placeholder {
      color: ${COLORS.textSecondary} !important;
    }
  }
  
  .ant-select-dropdown {
    background: ${COLORS.bgSecondary} !important;
    border: 1px solid ${COLORS.border} !important;
    
    .ant-select-item {
      background: ${COLORS.bgSecondary} !important;
      color: ${COLORS.textPrimary} !important;
      
      &:hover {
        background: ${COLORS.bgPrimary} !important;
      }
      
      &.ant-select-item-option-selected {
        background: ${COLORS.accent} !important;
        color: white !important;
      }
    }
  }
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const BalanceCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-body {
    padding: 24px !important;
  }
  
  .ant-statistic-title {
    color: ${COLORS.textSecondary} !important;
    font-size: 14px !important;
  }
  
  .ant-statistic-content {
    color: ${COLORS.textPrimary} !important;
    font-size: 32px !important;
    font-weight: 700 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 16px !important;
    }
  }
`;

const ActionButton = styled(Button)`
  height: 48px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  
  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 12px;
  }
`;

const WalletAddressDisplay = styled.div`
  background: ${COLORS.bgPrimary};
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  font-family: 'Courier New', monospace;
  font-size: 16px;
  color: ${COLORS.accent};
  word-break: break-all;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: ${COLORS.bgSecondary} !important;
    border: 1px solid ${COLORS.border} !important;
  }
  
  .ant-modal-header {
    background: ${COLORS.bgSecondary} !important;
    border-bottom: 1px solid ${COLORS.border} !important;
    
    .ant-modal-title {
      color: ${COLORS.textPrimary} !important;
    }
  }
  
  .ant-modal-close {
    color: ${COLORS.textSecondary} !important;
    
    &:hover {
      color: ${COLORS.textPrimary} !important;
    }
  }
  
  .ant-modal-body {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-modal-footer {
    background: ${COLORS.bgSecondary} !important;
    border-top: 1px solid ${COLORS.border} !important;
  }
  
  .ant-form-item-label > label {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-input,
  .ant-input-number,
  .ant-select-selector,
  .ant-input-number-input {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
`;

const Billing: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [walletBalance, setWalletBalance] = useState(0);
  const [internalWalletAddress, setInternalWalletAddress] = useState<string | null>(null);
  const [creditTransactions, setCreditTransactions] = useState<any[]>([]);
  const [walletTransactions, setWalletTransactions] = useState<any[]>([]);
  const [fundCreditModalVisible, setFundCreditModalVisible] = useState(false);
  
  // Subscription state
  const [subscription, setSubscription] = useState<any>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  
  // Resource usage state
  const [resourceUsage, setResourceUsage] = useState<any>(null);
  const [loadingResourceUsage, setLoadingResourceUsage] = useState(false);

  useEffect(() => {
    fetchBalances();
    fetchCreditTransactions();
    fetchWalletTransactions();
    fetchSubscription();
    fetchResourceUsage();
  }, []);

  const fetchSubscription = async () => {
    try {
      setLoadingSubscription(true);
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_SUBSCRIPTION || '/api/Merchant/lobby/billing/subscription');
      if (response.data?.status === 'success' && response.data?.data) {
        setSubscription(response.data.data);
      }
    } catch (error: any) {
      console.log('No subscription found');
      setSubscription(null);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const fetchResourceUsage = async () => {
    try {
      setLoadingResourceUsage(true);
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_RESOURCE_USAGE || '/api/Merchant/lobby/billing/resource-usage');
      if (response.data?.status === 'success' && response.data?.data) {
        setResourceUsage(response.data.data);
      }
    } catch (error: any) {
      console.log('No resource usage found');
      setResourceUsage(null);
    } finally {
      setLoadingResourceUsage(false);
    }
  };

  const fetchBalances = async () => {
    try {
      setLoading(true);
      
      // Fetch credit balance
      const creditResponse = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_CREDIT_BALANCE || '/api/Merchant/lobby/billing/credits');
      if (creditResponse.data?.status === 'success' && creditResponse.data?.data) {
        setCreditBalance(creditResponse.data.data.balance || 0);
      }

      // Fetch wallet balance and address
      const walletResponse = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_INTERNAL_WALLET || '/api/Merchant/lobby/billing/internal-wallet');
      if (walletResponse.data?.status === 'success' && walletResponse.data?.data) {
        setWalletBalance(parseFloat(walletResponse.data.data.balance?.toString() || '0'));
        // Get the EVM address for funding
        setInternalWalletAddress(walletResponse.data.data.wallet?.evmAddress || null);
      }
    } catch (error: any) {
      console.error('Error fetching balances:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditTransactions = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_CREDIT_TRANSACTIONS || '/api/Merchant/lobby/billing/credits/transactions');
      if (response.data?.status === 'success' && response.data?.data) {
        setCreditTransactions(Array.isArray(response.data.data) ? response.data.data : []);
      }
    } catch (error: any) {
      console.error('Error fetching credit transactions:', error);
    }
  };

  const fetchWalletTransactions = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_INTERNAL_WALLET_TRANSACTIONS || '/api/Merchant/lobby/billing/internal-wallet/transactions');
      if (response.data?.status === 'success' && response.data?.data) {
        setWalletTransactions(Array.isArray(response.data.data) ? response.data.data : []);
      }
    } catch (error: any) {
      console.error('Error fetching wallet transactions:', error);
    }
  };

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    message.success('Address copied to clipboard!');
  };

  const handleFundCredit = () => {
    setFundCreditModalVisible(true);
    form.resetFields();
  };

  const handleRedeemToken = async () => {
    try {
      const values = await form.validateFields();
      const response = await apiClient.post(
        API_ENDPOINTS.MERCHANT_REDEEM_CREDIT_TOKEN || '/api/Merchant/lobby/billing/credits/redeem-token',
        { tokenCode: values.tokenCode }
      );

      if (response.data?.status === 'success') {
        message.success('Credit token redeemed successfully!');
        setFundCreditModalVisible(false);
        form.resetFields();
        fetchBalances();
        fetchCreditTransactions();
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to redeem credit token');
    }
  };

  const creditColumns = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
      sorter: (a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Type',
      dataIndex: 'transactionType',
      key: 'transactionType',
      render: (type: string) => {
        const colors: Record<string, string> = {
          ADD: 'green',
          USE: 'orange',
          EXPIRED: 'red',
          REFUNDED: 'blue',
        };
        return <Tag color={colors[type] || 'default'}>{type}</Tag>;
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: any) => {
        const isNegative = record.transactionType === 'USE';
        return (
          <Text style={{ color: isNegative ? COLORS.warning : COLORS.success }}>
            {isNegative ? '-' : '+'}{formatMoney(amount)}
          </Text>
        );
      },
    },
    {
      title: 'Balance After',
      dataIndex: 'balanceAfter',
      key: 'balanceAfter',
      render: (balance: number) => formatMoney(balance),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
  ];

  const walletColumns = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
      sorter: (a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Type',
      dataIndex: 'transactionType',
      key: 'transactionType',
      render: (type: string) => {
        const colors: Record<string, string> = {
          DEPOSIT: 'green',
          WITHDRAWAL: 'orange',
          PAYMENT: 'red',
          REFUND: 'blue',
        };
        return <Tag color={colors[type] || 'default'}>{type}</Tag>;
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: any) => {
        const isNegative = ['WITHDRAWAL', 'PAYMENT'].includes(record.transactionType);
        return (
          <Text style={{ color: isNegative ? COLORS.warning : COLORS.success }}>
            {isNegative ? '-' : '+'}{formatMoney(amount)}
          </Text>
        );
      },
    },
    {
      title: 'Balance Before',
      dataIndex: 'balanceBefore',
      key: 'balanceBefore',
      render: (balance: number) => formatMoney(balance),
    },
    {
      title: 'Balance After',
      dataIndex: 'balanceAfter',
      key: 'balanceAfter',
      render: (balance: number) => formatMoney(balance),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, margin: 0 }}>
          Billing & Payments
        </Title>
      </HeaderContainer>

      {/* Subscription Overview */}
      {subscription && (
        <Card
          style={{
            background: 'linear-gradient(145deg, #1a1f2e 0%, #0d1117 100%)',
            border: `1px solid ${COLORS.border}`,
            marginBottom: 24,
          }}
        >
          <Spin spinning={loadingSubscription}>
            <Row gutter={[24, 16]} align="middle">
              <Col xs={24} md={8}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    background: subscription.subscriptionPlan?.name?.toUpperCase().includes('ENTERPRISE') 
                      ? 'linear-gradient(135deg, #A371F7 0%, #8957E5 100%)'
                      : subscription.subscriptionPlan?.name?.toUpperCase().includes('PRO')
                      ? 'linear-gradient(135deg, #58A6FF 0%, #1F6FEB 100%)'
                      : 'linear-gradient(135deg, #238636 0%, #2EA043 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    color: 'white',
                  }}>
                    <DollarOutlined />
                  </div>
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: COLORS.textPrimary }}>
                      {subscription.subscriptionPlan?.displayName || subscription.subscriptionPlan?.name || 'Current Plan'}
                    </div>
                    <Tag color={subscription.status === 'ACTIVE' ? 'green' : 'orange'}>
                      {subscription.status}
                    </Tag>
                  </div>
                </div>
              </Col>
              
              <Col xs={24} md={8}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>Monthly Fee</div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: COLORS.accent }}>
                    {formatMoney(subscription.effectivePrice || subscription.subscriptionPlan?.monthlyPrice || 0)}
                  </div>
                  {subscription.discountType && (
                    <Tag color="green" style={{ marginTop: '4px' }}>
                      {subscription.discountType === 'PERCENTAGE' 
                        ? `${formatNumber(subscription.discountValue)}% discount applied` 
                        : `${formatMoney(subscription.discountValue)} off`}
                    </Tag>
                  )}
                </div>
              </Col>
              
              <Col xs={24} md={8}>
                <div style={{ 
                  background: 'rgba(34, 211, 238, 0.1)', 
                  borderRadius: '12px', 
                  padding: '16px',
                  border: '1px solid rgba(34, 211, 238, 0.2)',
                }}>
                  <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>Next Billing</div>
                  <div style={{ fontSize: '18px', fontWeight: 600, color: COLORS.textPrimary }}>
                    {subscription.nextBillingDate 
                      ? dayjs(subscription.nextBillingDate).format('MMM DD, YYYY')
                      : 'Not scheduled'}
                  </div>
                  {subscription.nextBillingDate && (
                    <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginTop: '4px' }}>
                      in {dayjs(subscription.nextBillingDate).diff(dayjs(), 'day')} days
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            {/* Billing Insights */}
            <div style={{ 
              marginTop: '20px', 
              paddingTop: '16px', 
              borderTop: `1px solid ${COLORS.border}`,
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
            }}>
              {/* Payment readiness indicator */}
              {(() => {
                const totalBalance = creditBalance + walletBalance;
                const nextBillAmount = parseFloat(subscription.effectivePrice || 0);
                const isReady = totalBalance >= nextBillAmount;
                
                return (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    background: isReady ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                    border: `1px solid ${isReady ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                  }}>
                    {isReady ? <CheckCircleOutlined style={{ color: '#10B981' }} /> : <ClockCircleOutlined style={{ color: '#EF4444' }} />}
                    <span style={{ color: isReady ? '#10B981' : '#EF4444', fontSize: '13px', fontWeight: 500 }}>
                      {isReady 
                        ? 'Ready for next billing' 
                        : `Need ${formatMoney(nextBillAmount - totalBalance)} more for next billing`}
                    </span>
                  </div>
                );
              })()}
              
              {/* Auto-renew status */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                borderRadius: '8px',
                background: subscription.autoRenew ? 'rgba(59, 130, 246, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                border: `1px solid ${subscription.autoRenew ? 'rgba(59, 130, 246, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
              }}>
                <ReloadOutlined style={{ color: subscription.autoRenew ? '#3B82F6' : '#F59E0B' }} />
                <span style={{ color: subscription.autoRenew ? '#3B82F6' : '#F59E0B', fontSize: '13px', fontWeight: 500 }}>
                  {subscription.autoRenew ? 'Auto-renew enabled' : 'Auto-renew disabled'}
                </span>
              </div>
            </div>
          </Spin>
        </Card>
      )}

      {/* Resource Usage Section */}
      {subscription && resourceUsage && (
        <Card
          style={{
            background: COLORS.bgSecondary,
            border: `1px solid ${COLORS.border}`,
            marginBottom: 24,
          }}
          title={
            <Space>
              <PieChartOutlined style={{ color: COLORS.accent }} />
              <span style={{ color: COLORS.textPrimary }}>Resource Usage</span>
            </Space>
          }
          extra={
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchResourceUsage}
              loading={loadingResourceUsage}
              style={{ background: COLORS.bgPrimary, borderColor: COLORS.border, color: COLORS.textPrimary }}
            >
              Refresh
            </Button>
          }
        >
          <Spin spinning={loadingResourceUsage}>
            <Row gutter={[16, 16]}>
              {[
                { key: 'wallets', label: 'Wallets', color: '#3B82F6' },
                { key: 'staff', label: 'Staff Members', color: '#8B5CF6' },
                { key: 'blockchains', label: 'Blockchains', color: '#F59E0B' },
                { key: 'currencies', label: 'Currencies', color: '#10B981' },
                { key: 'contracts', label: 'Contracts', color: '#EC4899' },
              ].map((resource) => {
                const current = resourceUsage[resource.key]?.current || 0;
                const limit = resourceUsage[resource.key]?.limit;
                const isUnlimited = resourceUsage[resource.key]?.isUnlimited || limit === -1;
                const percentage = isUnlimited ? 0 : (limit > 0 ? Math.min(100, Math.round((current / limit) * 100)) : 0);
                const isOverage = !isUnlimited && limit > 0 && current > limit;
                
                return (
                  <Col xs={24} sm={12} md={8} key={resource.key}>
                    <div style={{
                      background: 'linear-gradient(145deg, #1a1f2e 0%, #0d1117 100%)',
                      borderRadius: '12px',
                      padding: '16px',
                      border: `1px solid ${isOverage ? '#EF4444' : COLORS.border}`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: COLORS.textSecondary, fontSize: '13px' }}>{resource.label}</span>
                        <span style={{ 
                          color: isOverage ? '#EF4444' : (percentage >= 80 ? '#F59E0B' : COLORS.textPrimary), 
                          fontWeight: 600, 
                          fontSize: '14px' 
                        }}>
                          {formatNumber(current)} / {isUnlimited ? '∞' : formatNumber(limit)}
                        </span>
                      </div>
                      <Progress 
                        percent={percentage} 
                        strokeColor={isOverage ? '#EF4444' : (percentage >= 80 ? '#F59E0B' : resource.color)}
                        trailColor={COLORS.border}
                        showInfo={false}
                        size="small"
                      />
                      {isOverage && (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#EF4444' }}>
                          <ExclamationCircleOutlined /> Overage: +{formatNumber(current - limit)}
                        </div>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Spin>
        </Card>
      )}

      {/* Plan Breakdown */}
      {subscription && subscription.subscriptionPlan && (
        <Card
          style={{
            background: COLORS.bgSecondary,
            border: `1px solid ${COLORS.border}`,
            marginBottom: 24,
          }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ color: COLORS.textPrimary }}>Your Plan Breakdown</span>
              <Button
                type="link"
                href="https://xend.switchwallet.io/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: COLORS.accent, padding: 0 }}
              >
                View Other Plans →
              </Button>
            </div>
          }
        >
          <div style={{ color: COLORS.textSecondary, marginBottom: 16, fontSize: '14px' }}>
            Here's what your current plan includes. To upgrade or change plans, contact your administrator or visit our pricing page.
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={4}>
              <div style={{
                textAlign: 'center',
                padding: '16px',
                background: 'rgba(34, 211, 238, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(34, 211, 238, 0.2)',
              }}>
                <div style={{ fontSize: '20px', fontWeight: 700, color: COLORS.accent, marginBottom: '4px' }}>
                  {subscription.subscriptionPlan.walletLimit !== null && subscription.subscriptionPlan.walletLimit !== undefined ? formatNumber(subscription.subscriptionPlan.walletLimit) : 'Unlimited'}
                </div>
                <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>Total Wallets</div>
              </div>
            </Col>

            <Col xs={24} sm={12} md={4}>
              <div style={{
                textAlign: 'center',
                padding: '16px',
                background: 'rgba(34, 211, 238, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(34, 211, 238, 0.2)',
              }}>
                <div style={{ fontSize: '20px', fontWeight: 700, color: COLORS.accent, marginBottom: '4px' }}>
                  {subscription.subscriptionPlan.staffLimit !== null && subscription.subscriptionPlan.staffLimit !== undefined ? formatNumber(subscription.subscriptionPlan.staffLimit) : 'Unlimited'}
                </div>
                <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>Staff Members</div>
              </div>
            </Col>

            <Col xs={24} sm={12} md={4}>
              <div style={{
                textAlign: 'center',
                padding: '16px',
                background: 'rgba(34, 211, 238, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(34, 211, 238, 0.2)',
              }}>
                <div style={{ fontSize: '20px', fontWeight: 700, color: COLORS.accent, marginBottom: '4px' }}>
                  {subscription.subscriptionPlan.currencyLimit !== null && subscription.subscriptionPlan.currencyLimit !== undefined ? formatNumber(subscription.subscriptionPlan.currencyLimit) : 'Unlimited'}
                </div>
                <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>Currencies</div>
              </div>
            </Col>

            <Col xs={24} sm={12} md={4}>
              <div style={{
                textAlign: 'center',
                padding: '16px',
                background: 'rgba(34, 211, 238, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(34, 211, 238, 0.2)',
              }}>
                <div style={{ fontSize: '20px', fontWeight: 700, color: COLORS.accent, marginBottom: '4px' }}>
                  {subscription.subscriptionPlan.blockchainLimit !== null && subscription.subscriptionPlan.blockchainLimit !== undefined ? formatNumber(subscription.subscriptionPlan.blockchainLimit) : 'Unlimited'}
                </div>
                <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>Blockchains</div>
              </div>
            </Col>

            <Col xs={24} sm={12} md={4}>
              <div style={{
                textAlign: 'center',
                padding: '16px',
                background: 'rgba(34, 211, 238, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(34, 211, 238, 0.2)',
              }}>
                <div style={{ fontSize: '20px', fontWeight: 700, color: COLORS.accent, marginBottom: '4px' }}>
                  {subscription.subscriptionPlan.contractLimit !== null && subscription.subscriptionPlan.contractLimit !== undefined ? formatNumber(subscription.subscriptionPlan.contractLimit) : 'Unlimited'}
                </div>
                <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>Smart Contracts</div>
              </div>
            </Col>
          </Row>

          {/* Additional Features */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${COLORS.border}` }}>
            <div style={{ marginBottom: 12, color: COLORS.textPrimary, fontWeight: 600 }}>Additional Features:</div>
            <Row gutter={[12, 8]}>
              <Col xs={24} sm={12}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircleOutlined style={{ color: COLORS.success }} />
                  <span style={{ color: COLORS.textPrimary, fontSize: '14px' }}>API Access</span>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircleOutlined style={{ color: COLORS.success }} />
                  <span style={{ color: COLORS.textPrimary, fontSize: '14px' }}>Real-time Webhooks</span>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {subscription.subscriptionPlan.supportsBitcoin ? (
                    <CheckCircleOutlined style={{ color: COLORS.success }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: COLORS.warning }} />
                  )}
                  <span style={{ color: COLORS.textPrimary, fontSize: '14px' }}>Bitcoin Support</span>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircleOutlined style={{ color: COLORS.success }} />
                  <span style={{ color: COLORS.textPrimary, fontSize: '14px' }}>Transaction Monitoring</span>
                </div>
              </Col>
            </Row>
          </div>
        </Card>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12}>
          <BalanceCard>
            <Statistic
              title="Credit Balance"
              value={creditBalance}
              prefix={<GiftOutlined />}
              precision={2}
              formatter={(value) => formatMoney(value as number)}
              valueStyle={{ color: COLORS.accent }}
            />
            <Paragraph style={{ color: COLORS.textSecondary, marginTop: 16, marginBottom: 0, fontSize: '12px' }}>
              Credits are used automatically for invoice payments. Redeem credit tokens to add credits.
            </Paragraph>
          </BalanceCard>
        </Col>
        <Col xs={24} sm={12}>
          <BalanceCard>
            <Statistic
              title="Internal Wallet Balance"
              value={walletBalance}
              prefix={<WalletOutlined />}
              precision={2}
              formatter={(value) => formatMoney(value as number)}
              valueStyle={{ color: COLORS.success }}
            />
            <Paragraph style={{ color: COLORS.textSecondary, marginTop: 16, marginBottom: 0, fontSize: '12px' }}>
              Wallet balance in USD. Fund your wallet by sending USDT, USDC, cNGN, or other supported currencies.
            </Paragraph>
          </BalanceCard>
        </Col>
      </Row>

      {/* Fund Internal Wallet Section */}
      {internalWalletAddress && (
        <Card
          style={{
            background: COLORS.bgSecondary,
            border: `1px solid ${COLORS.border}`,
            marginBottom: 24,
          }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <WalletOutlined style={{ fontSize: '24px', color: COLORS.success }} />
              <div>
                <Title level={4} style={{ color: COLORS.textPrimary, margin: 0 }}>
                  Fund Internal Wallet
                </Title>
                <Text style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
                  Send USDT or USDC on BSC (BNB Smart Chain) to fund your wallet
                </Text>
              </div>
            </div>
            
            <WalletAddressDisplay>
              <span>{internalWalletAddress}</span>
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={() => handleCopyAddress(internalWalletAddress)}
                style={{ color: COLORS.accent }}
              />
            </WalletAddressDisplay>

            <Alert
              message="Funding Instructions"
              description={
                <div style={{ fontSize: '13px' }}>
                  <div>• Send <strong>USDT</strong> or <strong>USDC</strong> on <strong>BSC (BNB Smart Chain)</strong> to the address above</div>
                  <div>• Funds will be automatically detected and converted to USD</div>
                  <div>• USD amount will be added to your internal wallet balance</div>
                </div>
              }
              type="info"
              showIcon
              style={{
                background: COLORS.bgPrimary,
                border: `1px solid ${COLORS.border}`,
              }}
            />
          </Space>
        </Card>
      )}

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
          marginBottom: 24,
        }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Title level={4} style={{ color: COLORS.textPrimary, margin: 0 }}>
            Quick Actions
          </Title>
          <Space wrap>
            <ActionButton
              type="primary"
              icon={<GiftOutlined />}
              onClick={handleFundCredit}
              style={{ backgroundColor: COLORS.accent, borderColor: COLORS.accent }}
            >
              Fund Credit (Redeem Token)
            </ActionButton>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchBalances();
                fetchCreditTransactions();
                fetchWalletTransactions();
              }}
            >
              Refresh
            </Button>
          </Space>
        </Space>
      </Card>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <Tabs defaultActiveKey="credits" style={{ color: COLORS.textPrimary }}>
          <TabPane
            tab={
              <span>
                <GiftOutlined />
                Credit Transactions
              </span>
            }
            key="credits"
          >
            <Table
              columns={creditColumns}
              dataSource={creditTransactions}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} transactions`,
              }}
              locale={{
                emptyText: <Empty description="No credit transactions yet" />,
              }}
            />
          </TabPane>
          <TabPane
            tab={
              <span>
                <WalletOutlined />
                Wallet Transactions
              </span>
            }
            key="wallet"
          >
            <Table
              columns={walletColumns}
              dataSource={walletTransactions}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} transactions`,
              }}
              locale={{
                emptyText: <Empty description="No wallet transactions yet" />,
              }}
            />
          </TabPane>
          <TabPane
            tab={
              <span>
                <DollarOutlined />
                Invoices
              </span>
            }
            key="invoices"
          >
            <InvoicesTab />
          </TabPane>
        </Tabs>
      </Card>

      {/* Fund Credit Modal */}
      <StyledModal
        title="Redeem Credit Token"
        open={fundCreditModalVisible}
        onOk={handleRedeemToken}
        onCancel={() => {
          setFundCreditModalVisible(false);
          form.resetFields();
        }}
        okText="Redeem Token"
        cancelText="Cancel"
        width={500}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="tokenCode"
            label="Credit Token Code"
            rules={[{ required: true, message: 'Please enter credit token code' }]}
            help="Enter the credit token code you received (e.g., CREDIT-XXXX-XXXX-XXXX)"
          >
            <Input
              placeholder="CREDIT-XXXX-XXXX-XXXX"
              style={{ fontFamily: 'monospace', textTransform: 'uppercase' }}
              onInput={(e: any) => {
                e.target.value = e.target.value.toUpperCase();
              }}
            />
          </Form.Item>
        </Form>
        <Alert
          message="How to get credit tokens"
          description="Credit tokens are provided by administrators. Contact your account manager or support to receive credit tokens."
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </StyledModal>
    </PageContainer>
  );
};

// Invoices Tab Component
const InvoicesTab: React.FC = () => {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        pageId: pagination.current.toString(),
        perPage: pagination.pageSize.toString(),
      });
      if (statusFilter) {
        params.append('status', statusFilter);
      }
      
      const response = await apiClient.get(
        `${API_ENDPOINTS.MERCHANT_GET_INVOICES || '/api/Merchant/lobby/billing/invoices'}?${params.toString()}`
      );
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setInvoices(data.invoices || []);
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total || 0,
        }));
      }
    } catch (error: any) {
      message.error('Failed to fetch invoices');
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, statusFilter]);

  const invoiceColumns = [
    {
      title: 'Invoice Number',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      render: (invoiceNumber: string) => (
        <Text strong style={{ color: COLORS.accent }}>{invoiceNumber}</Text>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_: any, record: any) => {
        const subtotal = parseFloat(record.subtotal?.toString() || '0');
        const vatAmount = parseFloat(record.vatAmount?.toString() || '0');
        const total = parseFloat(record.total.toString());
        const localCurrency = record.localCurrency;
        
        return (
          <div>
            <div style={{ fontWeight: 600, color: COLORS.textPrimary }}>
              {formatMoney(total)} USD
            </div>
            {vatAmount > 0 && (
              <div style={{ fontSize: '11px', color: COLORS.textSecondary, marginTop: '2px' }}>
                Subtotal: {formatMoney(subtotal)}
                {record.vatType === 'PERCENTAGE' && record.vatRate && (
                  <span> + VAT ({record.vatRate}%): {formatMoney(vatAmount)}</span>
                )}
                {record.vatType === 'FIXED' && (
                  <span> + VAT (Fixed): {formatMoney(vatAmount)}</span>
                )}
              </div>
            )}
            {localCurrency && (
              <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginTop: '2px' }}>
                {formatNumber(localCurrency.localCurrencyAmount)} ({localCurrency.localCurrency})
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig: Record<string, { color: string; icon: any; text: string }> = {
          PAID: { color: 'success', icon: <CheckCircleOutlined />, text: 'Paid' },
          PENDING: { color: 'warning', icon: <ClockCircleOutlined />, text: 'Pending' },
          FAILED: { color: 'error', icon: <CloseCircleOutlined />, text: 'Failed' },
          CANCELLED: { color: 'default', icon: <CloseCircleOutlined />, text: 'Cancelled' },
          DRAFT: { color: 'default', icon: <FileTextOutlined />, text: 'Draft' },
        };
        
        const config = statusConfig[status] || statusConfig.DRAFT;
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
      sorter: (a: any, b: any) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
    },
    {
      title: 'Paid At',
      dataIndex: 'paidAt',
      key: 'paidAt',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'Billing Period',
      key: 'billingPeriod',
      render: (_: any, record: any) => (
        <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>
          {dayjs(record.billingPeriodStart).format('MMM DD')} - {dayjs(record.billingPeriodEnd).format('MMM DD, YYYY')}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <Select
          placeholder="Filter by status"
          allowClear
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page when filter changes
          }}
          style={{ minWidth: 200 }}
          dropdownStyle={{
            background: COLORS.bgSecondary,
            border: `1px solid ${COLORS.border}`,
          }}
        >
          <Select.Option value="PENDING" style={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}>
            Pending
          </Select.Option>
          <Select.Option value="PAID" style={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}>
            Paid
          </Select.Option>
          <Select.Option value="DRAFT" style={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}>
            Draft
          </Select.Option>
          <Select.Option value="FAILED" style={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}>
            Failed
          </Select.Option>
          <Select.Option value="CANCELLED" style={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}>
            Cancelled
          </Select.Option>
        </Select>
      </div>
      <Table
        columns={invoiceColumns}
        dataSource={invoices}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} invoices`,
          onChange: (page, pageSize) => {
            setPagination(prev => ({ ...prev, current: page, pageSize }));
          },
        }}
        locale={{
          emptyText: <Empty description="No invoices yet" />,
        }}
      />
    </div>
  );
};

export default Billing;
